import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../ui/card';
import { settingsAPI, publicAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Upload, Image as ImageIcon, Building2, Save, X } from 'lucide-react';

const inputStyle = { width: '100%', padding: '0.6rem 0.85rem', borderRadius: 10, border: '1px solid #ECE7DE', background: '#fff', fontSize: '0.8125rem', color: '#002A38', outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748B', marginBottom: '0.375rem' };

export function CareersBrandingCard() {
  const [branding, setBranding] = useState({ company_name: '', blurb: '', brand_color: '#0076A3', logo_url: null, banner_url: null });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const logoRef = useRef(null);
  const bannerRef = useRef(null);

  useEffect(() => {
    settingsAPI.getBranding().then((res) => setBranding((b) => ({ ...b, ...res.data }))).catch(() => {});
  }, []);

  const set = (patch) => setBranding((b) => ({ ...b, ...patch }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await settingsAPI.saveBranding({ company_name: branding.company_name, blurb: branding.blurb, brand_color: branding.brand_color });
      set(res.data);
      toast.success('Careers branding saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save branding');
    } finally { setSaving(false); }
  };

  const handleUpload = async (kind, fileInput) => {
    const file = fileInput?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file (PNG, JPG, SVG or WebP)'); if (fileInput) fileInput.value = ''; return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); if (fileInput) fileInput.value = ''; return; }
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = kind === 'logo' ? await settingsAPI.uploadLogo(fd) : await settingsAPI.uploadBanner(fd);
      set(kind === 'logo' ? { logo_url: res.data.logo_url } : { banner_url: res.data.banner_url });
      toast.success(`${kind === 'logo' ? 'Logo' : 'Banner'} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(''); if (fileInput) fileInput.value = ''; }
  };

  const handleRemove = async (kind) => {
    setUploading(kind);
    try {
      await settingsAPI.removeBrandingAsset(kind);
      set(kind === 'logo' ? { logo_url: null } : { banner_url: null });
      toast.success(`${kind === 'logo' ? 'Logo' : 'Banner'} removed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    } finally { setUploading(''); }
  };

  const logoSrc = publicAPI.assetUrl(branding.logo_url);
  const bannerSrc = publicAPI.assetUrl(branding.banner_url);

  return (
    <Card data-testid="careers-branding-card" style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
      <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, color: '#0076A3', marginBottom: '0.35rem' }}>Careers Page Branding</h3>
      <p style={{ fontSize: '0.8125rem', color: '#64748B', marginBottom: '1.25rem' }}>Make your public job pages feel like your own site. Shown to candidates who apply.</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={labelStyle}>Display Company Name</label>
            <input data-testid="branding-company-name" value={branding.company_name} onChange={(e) => set({ company_name: e.target.value })} placeholder="Acme Inc." style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Short Blurb</label>
            <textarea data-testid="branding-blurb" value={branding.blurb} onChange={(e) => set({ blurb: e.target.value })} rows={3} placeholder="One or two sentences about your company and why people love working here." style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }} />
          </div>
          <div>
            <label style={labelStyle}>Brand Colour</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input data-testid="branding-color" type="color" value={branding.brand_color} onChange={(e) => set({ brand_color: e.target.value })} style={{ width: 44, height: 36, borderRadius: 8, border: '1px solid #ECE7DE', cursor: 'pointer', padding: 2, background: '#fff' }} />
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.8125rem', color: '#475569' }}>{branding.brand_color}</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={labelStyle}>Logo</label>
              <button data-testid="branding-logo-upload" type="button" onClick={() => logoRef.current?.click()} style={{ ...inputStyle, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#475569', textAlign: 'left' }}>
                <Upload size={14} /> {uploading === 'logo' ? 'Uploading…' : logoSrc ? 'Replace' : 'Upload'}
              </button>
              {logoSrc && <button data-testid="branding-logo-remove" type="button" onClick={() => handleRemove('logo')} style={{ marginTop: 4, background: 'none', border: 'none', color: '#DC2626', fontSize: '0.6875rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, padding: 0 }}><X size={11} /> Remove</button>}
              <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleUpload('logo', e.target)} />
            </div>
            <div>
              <label style={labelStyle}>Banner</label>
              <button data-testid="branding-banner-upload" type="button" onClick={() => bannerRef.current?.click()} style={{ ...inputStyle, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#475569', textAlign: 'left' }}>
                <Upload size={14} /> {uploading === 'banner' ? 'Uploading…' : bannerSrc ? 'Replace' : 'Upload'}
              </button>
              {bannerSrc && <button data-testid="branding-banner-remove" type="button" onClick={() => handleRemove('banner')} style={{ marginTop: 4, background: 'none', border: 'none', color: '#DC2626', fontSize: '0.6875rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, padding: 0 }}><X size={11} /> Remove</button>}
              <input ref={bannerRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleUpload('banner', e.target)} />
            </div>
          </div>
          <button data-testid="branding-save" onClick={handleSave} disabled={saving} style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0.6rem 1rem', borderRadius: 999, border: 'none', background: saving ? '#94A3B8' : '#0076A3', color: '#fff', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer' }}>
            <Save size={15} /> {saving ? 'Saving…' : 'Save branding'}
          </button>
        </div>

        {/* Live preview */}
        <div>
          <label style={labelStyle}>Preview</label>
          <div data-testid="branding-preview" style={{ border: '1px solid #ECE7DE', borderRadius: 12, overflow: 'hidden', background: '#FAF7F2' }}>
            <div style={{ height: 90, background: bannerSrc ? `center/cover no-repeat url(${bannerSrc})` : `linear-gradient(135deg, ${branding.brand_color}, ${branding.brand_color}99)` }} />
            <div style={{ padding: '0.9rem 1rem 1.1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: -34 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: '#fff', border: '1px solid #ECE7DE', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {logoSrc ? <img src={logoSrc} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Building2 size={22} color={branding.brand_color} />}
                </div>
              </div>
              <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 700, color: '#002A38', marginTop: 8 }}>{branding.company_name || 'Your Company'}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748B', marginTop: 4, lineHeight: 1.5 }}>{branding.blurb || 'Your short company blurb will appear here on public job pages.'}</div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 12, padding: '5px 12px', borderRadius: 999, background: branding.brand_color, color: '#fff', fontSize: '0.6875rem', fontWeight: 700 }}>
                <ImageIcon size={12} /> Apply now
              </span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
